import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Clock, Euro, Building, TrendingUp, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function AdminRecargos() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empresaExpandida, setEmpresaExpandida] = useState(null);
  const [detalleEmpresa, setDetalleEmpresa] = useState(null);
  const navigate = useNavigate();
  const { usuario, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (usuario?.rol !== 'admin') {
      navigate('/');
      return;
    }
    cargarRecargos();
  }, [authLoading, usuario]);

  const cargarRecargos = async () => {
    try {
      const response = await api.get('/admin/recargos');
      setEmpresas(response.data.data || []);
    } catch (error) {
      console.error('Error cargando recargos:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmpresa = async (empresaId) => {
    if (empresaExpandida === empresaId) {
      setEmpresaExpandida(null);
      setDetalleEmpresa(null);
      return;
    }

    setEmpresaExpandida(empresaId);
    try {
      const response = await api.get(`/admin/recargos/${empresaId}`);
      setDetalleEmpresa(response.data.data);
    } catch (error) {
      console.error('Error cargando detalle:', error);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0,00 EUR';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES');
  };

  // Calcular totales
  const totales = empresas.reduce((acc, e) => ({
    multas: acc.multas + (parseInt(e.total_multas) || 0),
    tardias: acc.tardias + (parseInt(e.multas_tardias) || 0),
    recargos: acc.recargos + (parseFloat(e.total_recargos) || 0)
  }), { multas: 0, tardias: 0, recargos: 0 });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-lg shadow-soft border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <ArrowLeft className="w-6 h-6 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-display">Recargos por Identificacion Tardia</h1>
            <p className="text-sm text-slate-600">Control de costes extra por identificacion fuera de plazo (mas de 20 dias)</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Resumen Global */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-primary-500">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary-100 rounded-xl">
                <Building className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Multas</p>
                <p className="text-3xl font-bold text-slate-900">{totales.multas}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-rose-500">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-rose-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Identificaciones Tardias</p>
                <p className="text-3xl font-bold text-rose-600">{totales.tardias}</p>
                <p className="text-xs text-slate-500">
                  {totales.multas > 0 ? ((totales.tardias / totales.multas) * 100).toFixed(1) : 0}% del total
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-amber-500">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Euro className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Recargos</p>
                <p className="text-3xl font-bold text-amber-600">{formatCurrency(totales.recargos)}</p>
                <p className="text-xs text-slate-500">20% del importe de multas tardias</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de Empresas */}
        <div className="bg-white rounded-xl shadow-soft overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900">Desglose por Empresa</h2>
          </div>

          {empresas.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
              <p>No hay multas con identificacion tardia</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {empresas.map((empresa) => (
                <div key={empresa.empresa_id}>
                  {/* Fila de empresa */}
                  <div
                    className={`px-6 py-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      parseFloat(empresa.total_recargos) > 0 ? 'bg-rose-50' : ''
                    }`}
                    onClick={() => toggleEmpresa(empresa.empresa_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          parseFloat(empresa.total_recargos) > 0 ? 'bg-rose-100' : 'bg-emerald-100'
                        }`}>
                          {parseFloat(empresa.total_recargos) > 0 ? (
                            <AlertTriangle className="w-5 h-5 text-rose-600" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{empresa.empresa_nombre}</p>
                          <p className="text-sm text-slate-500">{empresa.empresa_cif}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <p className="text-sm text-slate-500">Multas</p>
                          <p className="font-bold text-slate-900">{empresa.total_multas}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-slate-500">A Tiempo</p>
                          <p className="font-bold text-emerald-600">{empresa.multas_a_tiempo}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-slate-500">Tardias</p>
                          <p className={`font-bold ${parseInt(empresa.multas_tardias) > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                            {empresa.multas_tardias}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-slate-500">% Tardias</p>
                          <p className={`font-bold ${parseFloat(empresa.porcentaje_tardias) > 10 ? 'text-rose-600' : 'text-slate-700'}`}>
                            {empresa.porcentaje_tardias || 0}%
                          </p>
                        </div>
                        <div className="text-center min-w-[100px]">
                          <p className="text-sm text-slate-500">Recargos</p>
                          <p className={`font-bold ${parseFloat(empresa.total_recargos) > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                            {formatCurrency(empresa.total_recargos)}
                          </p>
                        </div>
                        <div>
                          {empresaExpandida === empresa.empresa_id ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detalle expandido */}
                  {empresaExpandida === empresa.empresa_id && detalleEmpresa && (
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                      <h3 className="font-medium text-slate-700 mb-3">Detalle de Multas</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-slate-500">
                              <th className="pb-2">Expediente</th>
                              <th className="pb-2">Matricula</th>
                              <th className="pb-2">F. Infraccion</th>
                              <th className="pb-2">F. Disposicion</th>
                              <th className="pb-2">F. Identificacion</th>
                              <th className="pb-2 text-center">Dias</th>
                              <th className="pb-2">Estado</th>
                              <th className="pb-2 text-right">Importe</th>
                              <th className="pb-2 text-right">Recargo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detalleEmpresa.multas.map((multa) => (
                              <tr key={multa.id} className={`border-t border-slate-200 ${multa.identificacion_tardia ? 'bg-rose-50' : ''}`}>
                                <td className="py-2 font-medium">{multa.numero_expediente}</td>
                                <td className="py-2">{multa.matricula}</td>
                                <td className="py-2">{formatDate(multa.fecha_infraccion)}</td>
                                <td className="py-2">{formatDate(multa.fecha_puesta_disposicion)}</td>
                                <td className="py-2">{formatDate(multa.fecha_identificacion)}</td>
                                <td className="py-2 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    multa.dias_para_identificar > 20
                                      ? 'bg-rose-100 text-rose-700'
                                      : multa.dias_para_identificar > 15
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-emerald-100 text-emerald-700'
                                  }`}>
                                    {multa.dias_para_identificar || '-'}
                                  </span>
                                </td>
                                <td className="py-2">
                                  {multa.identificacion_tardia ? (
                                    <span className="flex items-center gap-1 text-rose-600">
                                      <XCircle className="w-4 h-4" /> Tardia
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-emerald-600">
                                      <CheckCircle className="w-4 h-4" /> OK
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 text-right">{formatCurrency(multa.importe_multa)}</td>
                                <td className="py-2 text-right font-medium text-rose-600">
                                  {multa.coste_recargo > 0 ? formatCurrency(multa.coste_recargo) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Nota explicativa */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Plazo legal de identificacion</p>
              <p className="text-sm text-amber-700 mt-1">
                Las empresas tienen <strong>20 dias naturales</strong> desde la puesta a disposicion de la notificacion
                para identificar al conductor responsable. Pasado este plazo, se aplica un recargo del 20% sobre el importe de la multa.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminRecargos;
